import {
  Block,
  Button,
  Icon,
  List,
  ListItem,
  Page,
  Popup,
  Preloader,
} from "framework7-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import PopupNavbar from "../../components/popup-navbar";
import { usePopupBackClose } from "../../hooks/use-back-close";
import { useOnline } from "../../hooks/use-online";
import { loadRecurring } from "../../redux/reducer/recurring";
import { formatCents } from "../../utils/format";
import { berlinDateKey } from "../../utils/recurring";
import {
  RECURRING_TEXTS,
  describeNext,
  describeSchedule,
  recurringStateOf,
} from "../../utils/recurring-form";
import RecurringFormPopup from "./recurring-form-popup";
import "./recurring.css";

const INITIAL = { status: "idle", available: true, list: [], error: null };

const RecurringPopup = ({ groupId, participants = [], opened, onClose }) => {
  const dispatch = useDispatch();
  const online = useOnline();
  const backClose = usePopupBackClose(opened);
  const [state, setState] = useState(INITIAL);
  const [form, setForm] = useState({ opened: false, template: null });
  const latestRequest = useRef(0);
  const today = berlinDateKey(Date.now());

  const load = useCallback(() => {
    const request = ++latestRequest.current;
    setState((current) => ({ ...current, status: "loading", error: null }));
    dispatch(
      loadRecurring(
        groupId,
        (result) => {
          if (request !== latestRequest.current) return;
          setState({
            status: "loaded",
            available: result?.available !== false,
            list: result?.recurring ?? [],
            error: null,
          });
        },
        (message) => {
          if (request !== latestRequest.current) return;
          setState({ ...INITIAL, status: "error", error: message });
        },
      ),
    );
  }, [dispatch, groupId]);

  const needsLoad = state.status === "idle" || state.status === "offline";
  useEffect(() => {
    if (!opened) {
      latestRequest.current += 1;
      return;
    }
    if (!needsLoad || groupId == null) return;
    if (online) load();
    else setState((current) => (current.status === "offline" ? current : { ...INITIAL, status: "offline" }));
  }, [opened, online, needsLoad, groupId, load]);

  const showSaved = (result) => {
    latestRequest.current += 1;
    setState({
      status: "loaded",
      available: result?.available !== false,
      list: result?.recurring ?? [],
      error: null,
    });
  };

  const openForm = (template) => setForm({ opened: true, template });
  const { status, available, list } = state;
  const canCreate = online && status === "loaded" && available && participants.length > 0;

  return (
    <Popup
      {...backClose}
      className="recurring-popup"
      opened={opened}
      onPopupClosed={() => {
        setState(INITIAL);
        setForm((current) => ({ ...current, opened: false }));
        onClose();
      }}
    >
      <Page>
        <PopupNavbar title="Wiederkehrende Ausgaben" />
        <div className="recurring-content">
          {!online && (
            <Block key="offline" className="recurring-offline">
              {RECURRING_TEXTS.offline}
            </Block>
          )}
          {status === "loading" && (
            <Block key="loading" className="recurring-loading">
              <Preloader />
            </Block>
          )}
          {status === "error" && (
            <Block key="error" className="recurring-error">
              <p>{state.error}</p>
              <Button tonal onClick={load} disabled={!online}>
                Erneut versuchen
              </Button>
            </Block>
          )}
          {status === "loaded" && !available && (
            <Block key="unavailable" className="recurring-empty">
              {RECURRING_TEXTS.unavailable}
            </Block>
          )}
          {status === "loaded" && available && list.length === 0 && (
            <Block key="empty" className="recurring-empty">
              {RECURRING_TEXTS.empty}
            </Block>
          )}
          {list.length > 0 && (
            <List key="list" strong inset dividers mediaList className="recurring-list">
              {list.map((template) => (
                <ListItem
                  key={template.id}
                  link={online}
                  className={`recurring-item recurring-${recurringStateOf(template)}`}
                  title={template.title}
                  after={formatCents(template.amount)}
                  subtitle={describeSchedule(template)}
                  text={describeNext(template, today)}
                  onClick={() => {
                    if (online) openForm(template);
                  }}
                >
                  <Icon
                    slot="media"
                    ios={template.paused ? "f7:pause_circle" : "f7:repeat"}
                    md={template.paused ? "material:pause_circle" : "material:repeat"}
                  />
                </ListItem>
              ))}
            </List>
          )}
          {status === "loaded" && available && participants.length === 0 && (
            <Block key="no-participants" className="recurring-empty">
              {RECURRING_TEXTS.noParticipants}
            </Block>
          )}
          {(status === "loaded" || list.length > 0) && available && (
            <Block key="add" className="recurring-add">
              <Button fill disabled={!canCreate} onClick={() => openForm(null)}>
                Neue wiederkehrende Ausgabe
              </Button>
            </Block>
          )}
        </div>

        {/* Popup rule: one always-rendered wrapper per nested popup – see AGENTS.md. */}
        <div className="recurring-form-popup-wrapper">
          <RecurringFormPopup
            groupId={groupId}
            participants={participants}
            template={form.template}
            opened={form.opened}
            onSaved={showSaved}
            onClose={() => setForm((current) => ({ ...current, opened: false }))}
          />
        </div>
      </Page>
    </Popup>
  );
};

export default RecurringPopup;
